import { DocumentTextIcon, ExclamationTriangleIcon, ArrowPathIcon, DocumentArrowDownIcon } from "@heroicons/react/24/outline";
import { useLanguage } from "contexts/LanguageContext";
import { Template } from "types/template";
import { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { renderAsync } from 'docx-preview';
import toast from "react-hot-toast";

// Létrehozunk egy új típust, ami tartalmazza a minimálisan szükséges tulajdonságokat a preview-hoz
type PreviewTemplate = {
  fileUrl: string;
  fileName?: string;
}

interface DocumentPreviewProps {
  selectedTemplate: Template | PreviewTemplate | null;
  isLoadingPreview: boolean;
  setIsLoadingPreview: (value: boolean) => void;
}

const DocumentPreview = ({
  selectedTemplate,
  isLoadingPreview,
  setIsLoadingPreview
}: DocumentPreviewProps) => {
  const { t } = useLanguage();
  const [error, setError] = useState<string | null>(null);

  // Referenciák a dokumentum megjelenítéshez
  const docxContainerRef = useRef<HTMLDivElement>(null);
  const pdfContainerRef = useRef<HTMLObjectElement>(null);

  // Fájltípus felismerése a fájlnév alapján - memoizáljuk a függvény eredményét
  const getFileType = useCallback((url: string, fileName: string): string => {
    // Először a fájlnévből próbáljuk kinyerni a kiterjesztést
    const fileNameExt = fileName?.split('.').pop()?.toLowerCase();
    if (fileNameExt) {
      return fileNameExt;
    }

    // Ha nincs fájlnév, akkor az URL-ből próbáljuk kinyerni
    const filePathMatch = url.match(/\/([^/?]+)\?/);
    if (filePathMatch && filePathMatch[1]) {
      const decodedFileName = decodeURIComponent(filePathMatch[1]);
      const ext = decodedFileName.split('.').pop()?.toLowerCase();
      if (ext) {
        return ext;
      }
    }

    // Végső megoldásként az URL végét nézzük
    return url.split('.').pop()?.toLowerCase() || 'unknown';
  }, []);

  // Aktuális fájltípus memoizálása
  const currentFileType = useMemo(() => {
    if (!selectedTemplate?.fileUrl) return null;
    return getFileType(selectedTemplate.fileUrl, selectedTemplate.fileName || 'preview.docx');
  }, [selectedTemplate?.fileUrl, selectedTemplate?.fileName, getFileType]);

  // Automatikus méretezés beállítása
  const adjustDocumentSize = useCallback(() => {
    if (!docxContainerRef.current) return;

    const container = docxContainerRef.current;
    const containerWidth = container.clientWidth;
    const docxPages = container.querySelectorAll('.docx-viewer-page');

    docxPages.forEach((page: Element) => {
      const pageElement = page as HTMLElement;
      const naturalWidth = pageElement.scrollWidth;

      // A tartalmazó elem méretéhez igazítjuk a dokumentumot
      if (naturalWidth > containerWidth - 40) {
        const scale = (containerWidth - 40) / naturalWidth;
        pageElement.style.transform = `scale(${scale})`;
        pageElement.style.transformOrigin = 'top center';
        pageElement.style.marginBottom = `${Math.max(20, 30 * scale)}px`;
      } else {
        pageElement.style.transform = 'none';
      }
    });
  }, []);

  // DOCX dokumentumok megjelenítése
  const renderDocx = useCallback(async (fileUrl: string) => {
    if (!docxContainerRef.current) {
      console.error('docxContainerRef.current nem létezik');
      setError(t("documents.preview.error"));
      toast.error(t("documents.preview.error"));
      return;
    }

    try {
      setIsLoadingPreview(true);
      setError(null);

      // Fájl letöltése
      const response = await fetch(fileUrl);
      if (!response.ok) {
        throw new Error(`HTTP hiba: ${response.status}`);
      }

      const blob = await response.blob();

      // Ellenőrizzük újra, hogy a konténer még mindig létezik-e
      if (!docxContainerRef.current) {
        throw new Error('A dokumentum konténer nem elérhető');
      }

      // Tisztítjuk a konténert a renderelés előtt
      docxContainerRef.current.innerHTML = '';

      // Dokumentum megjelenítése docx-preview segítségével
      await renderAsync(blob, docxContainerRef.current, undefined, {
        className: 'docx-viewer',
        inWrapper: true,
        breakPages: true,
        ignoreLastRenderedPageBreak: true,
        ignoreWidth: false,
        ignoreHeight: false,
        ignoreFonts: false,
        hideWrapperOnPrint: true,
        experimental: false,
        trimXmlDeclaration: true,
        renderHeaders: true,
        renderFooters: true,
        renderFootnotes: true,
        renderEndnotes: true,
        renderChanges: false,
        renderComments: false,
        renderAltChunks: true,
        debug: process.env.NODE_ENV === 'development'
      });

      // Automatikus méretezés alkalmazása
      setTimeout(() => {
        adjustDocumentSize();
        setIsLoadingPreview(false);
        toast.success(t("documents.preview.loaded"));
      }, 200);

    } catch (err: any) {
      console.error('Hiba a dokumentum megjelenítése során:', err);
      setError(err.message || t("documents.preview.error"));
      toast.error(t("documents.preview.error"));
      setIsLoadingPreview(false);
    }
  }, [setIsLoadingPreview, t, adjustDocumentSize]);

  // Dokumentum újratöltése - memoizált callback
  const handleReload = useCallback(() => {
    if (!selectedTemplate?.fileUrl) return;

    const fileType = getFileType(selectedTemplate.fileUrl, selectedTemplate.fileName || 'preview.docx');

    if (fileType === 'docx') {
      if (docxContainerRef.current) {
        try {
          docxContainerRef.current.innerHTML = '';
          renderDocx(selectedTemplate.fileUrl);
          toast.success(t("documents.preview.reload"));
        } catch (err) {
          console.error('Hiba a dokumentum újratöltése során:', err);
          setError(t("documents.preview.error"));
          toast.error(t("documents.preview.error"));
        }
      }
    } else if (fileType === 'pdf') {
      setIsLoadingPreview(true);
      if (pdfContainerRef.current) {
        const src = pdfContainerRef.current.getAttribute('data');
        if (src) {
          pdfContainerRef.current.setAttribute('data', '');
          setTimeout(() => {
            pdfContainerRef.current?.setAttribute('data', src);
            setIsLoadingPreview(false);
            toast.success(t("documents.preview.reload"));
          }, 100);
        }
      }
    } else {
      setIsLoadingPreview(false);
    }
  }, [selectedTemplate, getFileType, renderDocx, setIsLoadingPreview, t]);

  // Indítjuk a betöltést, amikor a selectedTemplate változik
  useEffect(() => {
    if (selectedTemplate?.fileUrl) {
      const renderTimer = setTimeout(() => {
        const fileType = getFileType(selectedTemplate.fileUrl, selectedTemplate.fileName || 'preview.docx');

        if (fileType === 'docx') {
          renderDocx(selectedTemplate.fileUrl);
        } else {
          setIsLoadingPreview(false);
        }
      }, 100);

      return () => clearTimeout(renderTimer);
    }
  }, [selectedTemplate?.fileUrl, getFileType, renderDocx, setIsLoadingPreview]); // Csak a fileUrl változásra reagálunk

  // Ablakméret változásakor újraméretezzük a dokumentumot
  useEffect(() => {
    const handleResize = () => {
      if (docxContainerRef.current) {
        adjustDocumentSize();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [adjustDocumentSize]);

  return (
    <div className="w-full h-full flex flex-col">
      {/* 
        Globális stílusok a dokumentum helyes megjelenítéséhez
        - A next.js styled-jsx helyett inline style taget használunk
      */}
      <style dangerouslySetInnerHTML={{
        __html: `
        /* DocumentPreview globális stílusok - a dokumentum megfelelő skálázásához */
        .docx-viewer {
          width: 100% !important;
          height: 100% !important;
          overflow: auto !important;
        }
        
        .docx-wrapper {
          max-width: 100% !important;
          margin: 0 auto !important;
          padding: 10px !important;
          box-sizing: border-box !important;
          text-align: center !important;
        }
        
        .docx-viewer-page {
          display: inline-block !important;
          max-width: 100% !important;
          box-sizing: border-box !important;
          padding: 0 !important;
          margin: 0 auto 20px auto !important;
          border: 1px solid rgba(209, 213, 219, 0.3) !important;
          box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06) !important;
          transform-origin: top center !important;
        }
        
        /* Javítjuk a tartalmi elemek elhelyezkedését */
        .docx-viewer-page > div {
          width: 100% !important;
          max-width: 100% !important;
          overflow: visible !important;
        }
        
        /* Táblázatok megjelenítésének javítása */
        .docx-viewer table {
          max-width: 100% !important;
          table-layout: fixed !important;
          margin: 0 auto !important;
          border-collapse: collapse !important;
          border: 1px solid #ccc !important;
        }
        
        /* Táblázatcellák megfelelő tördelése */
        .docx-viewer table td, .docx-viewer table th {
          word-wrap: break-word !important;
          overflow-wrap: break-word !important;
          padding: 4px 8px !important;
          border: 1px solid #ddd !important;
          max-width: 250px !important;
          font-size: 90% !important;
        }
        
        /* Táblázatok megjelenítésének további javítása - POD táblák miatt */
        .docx-viewer w\:tbl, .docx-viewer .w-tbl {
          display: table !important;
          width: 100% !important;
          border-collapse: collapse !important;
          margin: 16px 0 !important;
        }
        
        .docx-viewer w\:tr, .docx-viewer .w-tr {
          display: table-row !important;
        }
        
        .docx-viewer w\:tc, .docx-viewer .w-tc {
          display: table-cell !important;
          padding: 4px 8px !important;
          border: 1px solid #ddd !important;
          vertical-align: top !important;
        }
        
        /* Táblázat fejléc stílusok */
        .docx-viewer w\:tr:first-child w\:tc, 
        .docx-viewer .w-tr:first-child .w-tc,
        .docx-viewer table tr:first-child td {
          background-color: #f5f5f5 !important;
          font-weight: bold !important;
        }
        
        /* Képek maximális szélessége */
        .docx-viewer img {
          max-width: 100% !important;
          height: auto !important;
        }
        
        /* PDF megjelenítés javítása */
        object[type="application/pdf"] {
          width: 100% !important;
          height: 100% !important;
          min-height: 300px !important;
        }
        
        /* Dokumentum görgetősáv stílusok */
        #docx-container-modal::-webkit-scrollbar {
          width: 8px !important;
          height: 8px !important;
        }
        
        #docx-container-modal::-webkit-scrollbar-track {
          background: #f9fafb !important;
        }
        
        #docx-container-modal::-webkit-scrollbar-thumb {
          background: #d1d5db !important;
          border-radius: 4px !important;
        }
        
        #docx-container-modal::-webkit-scrollbar-thumb:hover {
          background: #9ca3af !important;
        }
      ` }} />

      <div className="flex justify-between items-center mb-3 sm:mb-4">
        <h3 className="text-xs sm:text-sm font-medium text-gray-800">{t('documents.templatePreview')}</h3>
        {selectedTemplate && selectedTemplate.fileUrl && (
          <div className="flex items-center space-x-2">
            <button
              type="button"
              className="flex items-center px-2 py-1 text-xs rounded bg-gray-200 text-gray-700 hover:bg-gray-300"
              onClick={handleReload}
            >
              <ArrowPathIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1" />
              {t('common.reload')}
            </button>
          </div>
        )}
      </div>

      {!selectedTemplate || !selectedTemplate.fileUrl ? (
        <div className="flex-1 flex flex-col items-center justify-center p-4 sm:p-6 bg-white rounded-md border border-gray-200 min-h-[300px] md:min-h-0">
          <DocumentTextIcon className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mb-4" />
          <p className="text-gray-500 text-xs sm:text-sm text-center">
            {t('documents.selectTemplateDescription')}
          </p>
        </div>
      ) : (
        <div className="flex-1 bg-white rounded-md border border-gray-200 overflow-hidden relative min-h-[300px] md:min-h-0">
          {/* Betöltési állapot jelzése */}
          {isLoadingPreview && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-20">
              <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-teal-500"></div>
            </div>
          )}

          {/* Hibaüzenet megjelenítése */}
          {error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-20 p-4">
              <ExclamationTriangleIcon className="h-10 w-10 sm:h-12 sm:w-12 text-amber-500 mb-3 sm:mb-4" />
              <h2 className="text-base sm:text-lg font-medium text-gray-800 mb-2">{t("contracts.documentPreviewModal.documentLoadError")}</h2>
              <p className="text-gray-600 mb-4 text-xs sm:text-sm text-center">{error}</p>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                <button
                  onClick={handleReload}
                  className="flex items-center justify-center px-3 sm:px-4 py-1.5 sm:py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md transition-colors text-sm"
                >
                  <ArrowPathIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
                  {t("common.reload")}
                </button>
                <a
                  href={selectedTemplate.fileUrl}
                  download={selectedTemplate.fileName || "document"}
                  className="px-3 sm:px-4 py-1.5 sm:py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors inline-flex items-center justify-center text-sm"
                >
                  <DocumentArrowDownIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
                  {t("contracts.documentPreviewModal.download")}
                </a>
              </div>
            </div>
          )}

          {/* DOCX megjelenítő */}
          {selectedTemplate.fileUrl && currentFileType === 'docx' && (
            <div
              ref={docxContainerRef}
              className="w-full h-full bg-white overflow-auto px-1 py-1"
              id="docx-container-modal"
            ></div>
          )}

          {/* PDF megjelenítő */}
          {selectedTemplate.fileUrl && currentFileType === 'pdf' && (
            <object
              ref={pdfContainerRef}
              data={selectedTemplate.fileUrl}
              type="application/pdf"
              className="w-full h-full"
            >
              <p className="text-center p-4 text-gray-700 text-sm">
                {t("contracts.browserCannotDisplay")}
              </p>
            </object>
          )}

          {/* Nem támogatott fájlformátum - csak letöltési lehetőség */}
          {selectedTemplate.fileUrl &&
            currentFileType !== 'docx' &&
            currentFileType !== 'pdf' && (
              <div className="w-full h-full flex flex-col items-center justify-center bg-white p-4">
                <DocumentArrowDownIcon className="h-12 w-12 sm:h-16 sm:w-16 text-gray-400 mb-3 sm:mb-4" />
                <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2">
                  {t("contracts.unsupportedFileFormat")}
                </h2>
                <p className="text-gray-500 mb-4 sm:mb-6 text-center max-w-md text-xs sm:text-sm">
                  {t("contracts.cannotPreviewThisFormat")}
                </p>
                <a
                  href={selectedTemplate.fileUrl}
                  download={selectedTemplate.fileName || "document"}
                  className="px-4 sm:px-6 py-2 sm:py-3 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors inline-flex items-center text-sm sm:text-base justify-center"
                >
                  <DocumentArrowDownIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2" />
                  {t("contracts.documentPreviewModal.download")}
                </a>
                <p className="text-gray-500 mt-3 sm:mt-4 text-xs sm:text-sm">
                  {selectedTemplate.fileName}
                </p>
              </div>
            )}
        </div>
      )}
    </div>
  );
};

export default DocumentPreview;